import React, {useState, useMemo, createRef} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    Button,
    useToast,
    Alert,
} from '@spothero/ui';
import {useForm} from 'react-hook-form';
import VehicleSelect, {
    VEHICLE_FIELD,
    ADD_NEW_VEHICLE_VALUE,
} from '../vehicle-select';
import PayOversize from '../pay-oversize';
import {PROPTYPE_RESERVATION} from '@/proptypes/index';
import {getTransformedUserVehicles} from 'store/user/user-selectors';
import {usePaymentForm, CARD_FORM_ID} from 'common/payment-picker';
import trackReservationUpdated, {
    LOCATIONS,
    UPDATED_FIELDS,
} from 'segment/events/reservation-updated';
import {VEHICLE_FORM_ID} from '../vehicle-select/VehicleSelect';
import OversizeRefund, {REFUND_FIELD, REFUND_FORM_ID} from '../oversize-refund';
import trackReservationVehicleUpdated from 'segment/events/reservation-vehicle-updated';
import trackReservationVehicleUpdateFailed from 'segment/events/reservation-vehicle-update-failed';

// Views
const VEHICLE_SELECT_VIEW = 'VEHICLE_SELECT_VIEW';
const PAYMENT_VIEW = 'PAYMENT_VIEW';
const REFUND_VIEW = 'REFUND_VIEW';

const ChangeVehicleModal = ({
    reservation,
    onClose,
    isOpen,
    activeStatus,
    onTrackedClick,
}) => {
    const [currentView, setCurrentView] = useState(VEHICLE_SELECT_VIEW);
    const vehicles = useSelector(getTransformedUserVehicles);
    const user = useSelector(state => state.user.data);
    const [rateDiffData, setRateDiffData] = useState(null);
    const toast = useToast();

    const addToast = () => {
        const toastRef = createRef();

        const close = () => {
            if (toastRef.current) {
                toast.close(toastRef.current);
            }
        };

        toastRef.current = toast({
            position: 'top',
            duration: 3000,
            render: () => (
                <Alert
                    title="Vehicle updated"
                    status="success"
                    showIcon
                    showDismiss
                    onDismissClick={close}
                />
            ),
        });
    };

    const allVehicles = useMemo(() => {
        if (
            reservation.vehicle?.vehicleProfileId &&
            !vehicles.find(
                vehicle =>
                    vehicle.vehicleProfileId ===
                    reservation.vehicle.vehicleProfileId
            )
        ) {
            return [reservation.vehicle, ...vehicles];
        }

        return vehicles;
    }, [vehicles, reservation.vehicle]);

    const getInitialSelectedVehicle = () => {
        if (reservation.vehicle?.vehicleProfileId) {
            return reservation.vehicle.vehicleProfileId;
        } else if (allVehicles.length > 0) {
            const defaultVehicle = allVehicles.find(
                vehicle => vehicle.isDefault
            );

            if (defaultVehicle) {
                return defaultVehicle.vehicleProfileId;
            } else {
                return allVehicles[0].vehicleProfileId;
            }
        } else {
            return ADD_NEW_VEHICLE_VALUE;
        }
    };

    // useState with a callback to compute a derived value from props + store state only on mount.
    // Feels slightly funky because never updating the state, but does the trick.
    // Could also have used useMemo, but the computation isn't expensive and we don't want to run it more than first time
    const [initialSelectedVehicleValue] = useState(getInitialSelectedVehicle);

    // Initialize the vehicle form with default values
    const {
        control: vehicleControl,
        handleSubmit: handleVehicleSubmit,
        setError: setVehicleError,
        reset: resetVehicleFormState,
        formState: {
            errors: vehicleErrors,
            isSubmitting: vehicleIsSubmitting,
            dirtyFields: dirtyVehicleFields,
        },
    } = useForm({
        defaultValues: {
            [VEHICLE_FIELD]: initialSelectedVehicleValue,
        },
    });

    // Initializing all of these forms in the parent isn't ideal. Would much prefer to do so in child,
    // but parent needs some of the props, like handling submit and resetting state
    const {
        control: refundControl,
        handleSubmit: handleRefundSubmit,
        setError: setRefundError,
        reset: resetRefundFormState,
        formState: {errors: refundErrors, isSubmitting: refundIsSubmitting},
        register: refundRegister,
    } = useForm();

    const {
        control: cardControl,
        handleSubmit: handleCardSubmit,
        setError: setCardError,
        reset: resetCardFormState,
        formState: {errors: cardErrors, isSubmitting: cardIsSubmitting},
    } = usePaymentForm();

    const resetModalState = () => {
        setCurrentView(VEHICLE_SELECT_VIEW);
        setRateDiffData({});
        resetVehicleFormState({[VEHICLE_FIELD]: getInitialSelectedVehicle()});
        resetCardFormState();
        resetRefundFormState();
    };

    const handleSuccess = (reservationPriceChanged = 0) => {
        trackReservationUpdated({
            reservation,
            updatedProperties: [UPDATED_FIELDS.VEHICLE],
            location: LOCATIONS.PARKING_PASS,
            reservationPriceChanged,
            activeStatus,
            user,
        });
        onClose();
        addToast();
    };

    const handleRateDiff = ({vehicle, rateDiff}) => {
        if (rateDiff.originalRental.price > rateDiff.priceBreakdown.price) {
            setRateDiffData({rateDiff, vehicle});
            setCurrentView(REFUND_VIEW);
        } else if (
            rateDiff.originalRental.price < rateDiff.priceBreakdown.price
        ) {
            setRateDiffData({rateDiff, vehicle});
            setCurrentView(PAYMENT_VIEW);
        }
    };

    const handleReservationVehicleUpdated = ({
        priceDifference,
        newVehicle,
        view,
    }) => {
        trackReservationVehicleUpdated({
            reservation,
            user,
            priceDifference,
            newVehicle,
            view,
        });
    };

    const handleReservationVehicleUpdateFailed = ({
        priceDifference,
        newVehicle,
        view,
        errors,
    }) => {
        trackReservationVehicleUpdateFailed({
            reservation,
            user,
            priceDifference,
            newVehicle,
            view,
            errors,
        });
    };

    const handleChangeVehicleSubmit = () => {
        const location =
            currentView === PAYMENT_VIEW
                ? 'Payment Modal Submit'
                : currentView === REFUND_VIEW
                ? 'Refund Modal Submit'
                : 'Change Vehicle Modal Submit';

        onTrackedClick({
            clickTarget: location,
            isReservationChange: true,
        });
    };

    const vehicleSubmitDisabled =
        vehicleIsSubmitting ||
        (!dirtyVehicleFields[VEHICLE_FIELD] &&
            initialSelectedVehicleValue ===
                reservation.vehicle?.vehicleProfileId);

    return (
        <Modal
            hasHeader
            hasFooter
            size="md"
            isOpen={isOpen}
            hideCloseButton={
                vehicleIsSubmitting || cardIsSubmitting || refundIsSubmitting
            }
            closeOnOverlayClick={false}
            onClose={onClose}
            onCloseComplete={resetModalState}
        >
            <ModalHeader>Change Vehicle</ModalHeader>
            <ModalBody>
                {currentView === VEHICLE_SELECT_VIEW && (
                    <VehicleSelect
                        vehicles={allVehicles}
                        control={vehicleControl}
                        handleSubmit={handleVehicleSubmit}
                        reservation={reservation}
                        handleSuccess={handleSuccess}
                        setError={setVehicleError}
                        error={vehicleErrors[VEHICLE_FIELD]}
                        handleRateDiff={handleRateDiff}
                        handleReservationVehicleUpdated={
                            handleReservationVehicleUpdated
                        }
                        handleReservationVehicleUpdateFailed={
                            handleReservationVehicleUpdateFailed
                        }
                    />
                )}
                {currentView === PAYMENT_VIEW && (
                    <PayOversize
                        rateDiff={rateDiffData.rateDiff}
                        vehicle={rateDiffData.vehicle}
                        control={cardControl}
                        handleSubmit={handleCardSubmit}
                        setError={setCardError}
                        errors={cardErrors}
                        reservationId={reservation.rentalId}
                        handleSuccess={handleSuccess}
                        handleReservationVehicleUpdated={
                            handleReservationVehicleUpdated
                        }
                        handleReservationVehicleUpdateFailed={
                            handleReservationVehicleUpdateFailed
                        }
                    />
                )}
                {currentView === REFUND_VIEW && (
                    <OversizeRefund
                        rateDiff={rateDiffData.rateDiff}
                        control={refundControl}
                        handleSubmit={handleRefundSubmit}
                        setError={setRefundError}
                        error={refundErrors[REFUND_FIELD]}
                        reservation={reservation}
                        handleSuccess={handleSuccess}
                        vehicle={rateDiffData.vehicle}
                        register={refundRegister}
                        handleReservationVehicleUpdated={
                            handleReservationVehicleUpdated
                        }
                        handleReservationVehicleUpdateFailed={
                            handleReservationVehicleUpdateFailed
                        }
                    />
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={onClose}
                    variant="secondary"
                    marginRight={4}
                    fontFamily="body"
                    disabled={
                        vehicleIsSubmitting ||
                        cardIsSubmitting ||
                        refundIsSubmitting
                    }
                    data-testid="ChangeVehicle-button-cancel"
                >
                    Cancel
                </Button>
                <Button
                    form={
                        {
                            [VEHICLE_SELECT_VIEW]: VEHICLE_FORM_ID,
                            [PAYMENT_VIEW]: CARD_FORM_ID,
                            [REFUND_VIEW]: REFUND_FORM_ID,
                        }[currentView]
                    }
                    type="submit"
                    variant="primary"
                    fontFamily="body"
                    disabled={
                        currentView === VEHICLE_SELECT_VIEW
                            ? vehicleSubmitDisabled
                            : cardIsSubmitting || refundIsSubmitting
                    }
                    data-testid="ChangeVehicle-button-submit"
                    onClick={handleChangeVehicleSubmit}
                >
                    {currentView === VEHICLE_SELECT_VIEW
                        ? 'Submit'
                        : 'Complete Change'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

ChangeVehicleModal.propTypes = {
    reservation: PROPTYPE_RESERVATION,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    activeStatus: PropTypes.string.isRequired,
    onTrackedClick: PropTypes.func.isRequired,
};

export default ChangeVehicleModal;
